import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { toastService } from '../../../../utils/toastService';
import {
    useLazyGetInternalRetailPartsQuery,
    useCreateInternalRetailTransferMutation
} from '../../../../api/endpoints/parts';
import InternalRetailSubDealerDropdown from './InternalRetailSubDealerDropdown';

const InternalRetailPartsAddDialog = ({ visible, onHide, onSuccess }) => {
    const { th } = useLanguage();
    const browserLocale = navigator.language || 'tr-TR';
    const debounceTimerRef = useRef(null);

    // State
    const [selectedSubDealer, setSelectedSubDealer] = useState(null);
    const [selectedParts, setSelectedParts] = useState([]);
    const [partSearchText, setPartSearchText] = useState('');
    const [partFilterValue, setPartFilterValue] = useState('');
    const [selectedPartId, setSelectedPartId] = useState(null);

    // Lazy query for parts dropdown
    const [fetchPartsForDropdown, { data: partsData = [], isFetching: isFetchingParts }] = useLazyGetInternalRetailPartsQuery();
    
    // Separate lazy query for updating selected parts (doesn't affect dropdown)
    const [fetchPartsForUpdate] = useLazyGetInternalRetailPartsQuery();

    // Create mutation
    const [createTransfer, { isLoading: isCreating }] = useCreateInternalRetailTransferMutation();

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    // Reset state when dialog closes
    useEffect(() => {
        if (!visible) {
            setSelectedSubDealer(null);
            setSelectedParts([]);
            setPartSearchText('');
            setPartFilterValue('');
            setSelectedPartId(null);
        }
    }, [visible]);

    // Fetch parts for dropdown when sub-dealer is selected and search text changes
    useEffect(() => {
        if (selectedSubDealer && visible) {
            fetchPartsForDropdown({
                PartCode: partSearchText,
                CustomerId: selectedSubDealer,
                RowCount: 50,
                SelectedParts: [] // Always empty for dropdown search
            });
        }
    }, [selectedSubDealer, partSearchText, visible, fetchPartsForDropdown]);

    // Handle sub-dealer change
    const handleSubDealerChange = useCallback((newDealerId) => {
        setSelectedSubDealer(newDealerId);
        setPartSearchText(''); // Reset search - will trigger useEffect to fetch dropdown
        setPartFilterValue('');
        
        // If there are selected parts, send SEPARATE request to update their info
        if (newDealerId && selectedParts.length > 0) {
            const selectedPartCodes = selectedParts.map(p => p.partCode);
            // Separate request for selected parts - doesn't affect partsData (dropdown)
            fetchPartsForUpdate({
                PartCode: '',
                CustomerId: newDealerId,
                RowCount: 50,
                SelectedParts: selectedPartCodes
            }).then((result) => {
                if (result.data) {
                    // Update existing parts with new data (keeping user-entered quantities)
                    setSelectedParts(prev => prev.map(part => {
                        const updatedInfo = result.data.find(p => p.partCode === part.partCode);
                        if (updatedInfo) {
                            return {
                                ...part,
                                partName: updatedInfo.partName,
                                availableQuantity: updatedInfo.availableQuantity,
                                unitPrice: updatedInfo.unitPrice,
                                currency: updatedInfo.currency
                            };
                        }
                        return part;
                    }));
                }
            });
        }
    }, [selectedParts, fetchPartsForUpdate]);

    // Part dropdown options
    const partDropdownOptions = useMemo(() => {
        if (!partsData || partsData.length === 0) return [];
        
        // Filter out already selected parts
        const selectedCodes = selectedParts.map(p => p.partCode);
        return partsData
            .filter(p => !selectedCodes.includes(p.partCode))
            .map(p => ({
                label: `${p.partCode} - ${p.partName}`,
                value: p.partCode,
                ...p
            }));
    }, [partsData, selectedParts]);

    // Handle part search input with debounce
    const handlePartFilterInputChange = (val) => {
        setPartFilterValue(val);
        
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        
        debounceTimerRef.current = setTimeout(() => {
            setPartSearchText(val);
        }, 500);
    };

    // Custom filter template for part dropdown
    const partFilterTemplate = useMemo(() => (
        <div className="p-dropdown-filter-container">
            <InputText
                value={partFilterValue}
                onChange={(e) => handlePartFilterInputChange(e.target.value)}
                className="p-dropdown-filter w-full"
                placeholder={th('common.search') || 'Ara...'}
                autoFocus
                type="text"
                inputMode="text"
            />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    ), [partFilterValue, th]);

    // Handle part selection
    const handlePartSelect = (partCode) => {
        if (!partCode) return;
        
        const selectedItem = partsData.find(p => p.partCode === partCode);
        if (!selectedItem) return;

        setSelectedParts(prev => [...prev, {
            ...selectedItem,
            quantity: 1,
            _id: `${selectedItem.partCode}_${Date.now()}`
        }]);
        
        setSelectedPartId(null);
        setPartFilterValue('');
        setPartSearchText('');
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };

    // Remove part from selection
    const handleRemovePart = (id) => {
        setSelectedParts(prev => prev.filter(p => p._id !== id));
    };

    // Update part quantity
    const handleQuantityChange = (id, newQuantity) => {
        setSelectedParts(prev => prev.map(p => 
            p._id === id ? { ...p, quantity: newQuantity } : p
        ));
    };

    // Calculate total amount
    const totalAmount = useMemo(() => {
        return selectedParts.reduce((sum, part) => {
            return sum + (part.unitPrice * (part.quantity || 0));
        }, 0);
    }, [selectedParts]);

    // Get first currency from parts (assuming all same currency)
    const currency = selectedParts.length > 0 ? selectedParts[0].currency : '';

    // Handle save
    const handleSave = async () => {
        if (!selectedSubDealer) {
            toastService.error(th('validation.subDealerRequired') || 'Alt bayi seçiniz');
            return;
        }

        if (selectedParts.length === 0) {
            toastService.error(th('validation.partsRequired') || 'En az bir parça eklemelisiniz');
            return;
        }

        const invalidParts = selectedParts.filter(p => !p.quantity || p.quantity <= 0);
        if (invalidParts.length > 0) {
            toastService.error(th('validation.quantityRequired') || 'Tüm parçalar için miktar giriniz');
            return;
        }

        const payload = {
            parts: selectedParts.map(p => ({
                partCode: p.partCode,
                quantity: p.quantity
            })),
            reciverDealerId: selectedSubDealer
        };

        try {
            await createTransfer(payload).unwrap();
            toastService.success(th('common.success') || 'İşlem başarılı');
            onSuccess?.();
        } catch (error) {
            console.error('Create transfer error:', error);
            toastService.error(th('common.error') || 'Bir hata oluştu');
        }
    };

    // Quantity editor for table
    const quantityEditor = (rowData) => (
        <InputNumber
            value={rowData.quantity}
            onValueChange={(e) => handleQuantityChange(rowData._id, e.value ?? 0)}
            min={0}
            max={rowData.availableQuantity}
            mode="decimal"
            maxFractionDigits={2}
            locale={browserLocale}
            inputClassName="w-full"
            className="w-full"
            style={{ maxWidth: '100px' }}
        />
    );

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const footer = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex align-items-center gap-2 p-2 surface-100 border-round">
                <span className="font-semibold">{th('internalRetailParts.totalAmount') || 'Toplam Tutar'}:</span>
                <span className="text-lg font-bold text-primary">
                    {formatCurrency(totalAmount)} {currency}
                </span>
            </div>
            <div className="flex gap-2">
                <Button
                    label={th('common.cancel') || 'Vazgeç'}
                    icon="pi pi-times"
                    severity="secondary"
                    onClick={onHide}
                />
                <Button
                    label={th('common.save') || 'Kaydet'}
                    icon="pi pi-save"
                    severity="success"
                    onClick={handleSave}
                    loading={isCreating}
                    disabled={selectedParts.length === 0 || !selectedSubDealer}
                />
            </div>
        </div>
    );

    return (
        <Dialog
            header={th('internalRetailParts.addTitle') || 'Yeni Transfer Oluştur'}
            visible={visible}
            style={{ width: '90vw', maxWidth: '1100px' }}
            modal
            onHide={onHide}
            footer={footer}
        >
            <div className="p-fluid grid">
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-semibold">
                        {th('internalRetailParts.subDealer') || 'Alt Bayi'} *
                    </label>
                    <InternalRetailSubDealerDropdown
                        value={selectedSubDealer}
                        onChange={handleSubDealerChange}
                        showClear
                        placeholder={th('internalRetailParts.selectSubDealer') || 'Alt bayi seçiniz'}
                    />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-semibold">
                        {th('parts.selectPart') || 'Parça Seçiniz'}
                    </label>
                    <Dropdown
                        value={selectedPartId}
                        options={partDropdownOptions}
                        onChange={(e) => {
                            setSelectedPartId(e.value);
                            handlePartSelect(e.value);
                        }}
                        optionLabel="label"
                        optionValue="value"
                        placeholder={th('parts.selectPart') || 'Parça seçiniz'}
                        filter
                        filterTemplate={partFilterTemplate}
                        showClear
                        loading={isFetchingParts}
                        disabled={!selectedSubDealer}
                        className="w-full"
                        emptyMessage={th('common.noRecords') || 'Kayıt bulunamadı'}
                        emptyFilterMessage={th('common.noRecords') || 'Kayıt bulunamadı'}
                        panelStyle={{ maxWidth: '600px' }}
                    />
                </div>
            </div>

            <div className="mt-4">
                <DataTable
                    value={selectedParts}
                    dataKey="_id"
                    emptyMessage={th('internalRetailParts.noPartsSelected') || 'Henüz parça eklenmedi'}
                    stripedRows
                    showGridlines
                    size="small"
                >
                    <Column
                        header="#"
                        body={(row, opts) => opts.rowIndex + 1}
                        style={{ width: '50px', textAlign: 'center' }}
                    />
                    <Column
                        field="partCode"
                        header={th('product.code') || 'Parça Kodu'}
                        style={{ width: '140px' }}
                    />
                    <Column
                        field="partName"
                        header={th('product.name') || 'Parça Adı'}
                    />
                    <Column
                        field="availableQuantity"
                        header={th('internalRetailParts.availableQuantity') || 'Mevcut Stok'}
                        style={{ width: '120px', textAlign: 'right' }}
                        body={(row) => formatCurrency(row.availableQuantity)}
                    />
                    <Column
                        field="unitPrice"
                        header={th('basket.price') || 'Birim Fiyat'}
                        style={{ width: '130px', textAlign: 'right' }}
                        body={(row) => `${formatCurrency(row.unitPrice)} ${row.currency || ''}`}
                    />
                    <Column
                        header={th('product.quantity') || 'Miktar'}
                        body={quantityEditor}
                        style={{ width: '130px', backgroundColor: '#f0f8ff' }}
                    />
                    <Column
                        header={th('internalRetailParts.lineTotal') || 'Satır Toplamı'}
                        body={(row) => `${formatCurrency(row.unitPrice * (row.quantity || 0))} ${row.currency || ''}`}
                        style={{ width: '150px', textAlign: 'right' }}
                    />
                    <Column
                        header=""
                        body={(row) => (
                            <Button
                                icon="pi pi-trash"
                                rounded
                                text
                                severity="danger"
                                onClick={() => handleRemovePart(row._id)}
                            />
                        )}
                        style={{ width: '60px' }}
                    />
                </DataTable>
            </div>
        </Dialog>
    );
};

export default InternalRetailPartsAddDialog;

